import React from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import {
  Avatar,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Person24Regular,
  Alert24Regular,
  SignOut24Regular,
  Settings24Regular,
  ChevronDown16Regular,
  Bug24Regular,
} from '@fluentui/react-icons';
import { usePortalAuth } from '../contexts/PortalAuthContext';
import { PortalDebugProvider, usePortalDebug } from '../contexts/PortalDebugContext';
import { ChatProvider, useChat } from '../../contexts/ChatContext';
import ChatWindow from '../../components/ChatWindow';
import MinimizedChat from '../../components/MinimizedChat';
import '../styles/portal.css';

const useStyles = makeStyles({
  headerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textDecoration: 'none',
    color: 'inherit',
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  debugButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', 'transparent'),
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: 'monospace',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  debugButtonActive: {
    backgroundColor: '#fff4ce',
    color: '#986f0b',
    ...shorthands.borderColor('#f0d060'),
    ':hover': {
      backgroundColor: '#fef0b0',
      color: '#986f0b',
    },
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
});

const PortalLayoutInner: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user, logout } = usePortalAuth();
  const { debugMode, toggleDebug } = usePortalDebug();
  const { openChat, openChatWindows, minimizedChats, closeChat, minimizeChat, closeMinimizedChat } = useChat();

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  return (
    <div className="portal-container">
      {/* Header */}
      <header className="portal-header">
        <div className="portal-header-left">
          <Link to="/portal" className={styles.headerLogo}>
            <img
              src="/logo-tradewinds.png"
              alt="Tradewinds Mechanical"
              className="portal-header-logo"
            />
            <span className={styles.headerTitle}>Customer Portal</span>
          </Link>
        </div>

        <div className="portal-header-right">
          <Tooltip content="Notifications" relationship="label">
            <Button
              appearance="subtle"
              icon={<Alert24Regular />}
              aria-label="Notifications"
            />
          </Tooltip>

          <Tooltip content={debugMode ? 'Exit debug mode' : 'Debug mode'} relationship="label">
            <Button
              appearance="subtle"
              icon={<Bug24Regular />}
              aria-label="Toggle debug mode"
              onClick={toggleDebug}
              style={debugMode ? { backgroundColor: '#fff4ce', color: '#986f0b' } : undefined}
            />
          </Tooltip>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <div className={styles.userButton}>
                <Avatar
                  name={user?.name || 'User'}
                  size={32}
                  color="colorful"
                />
                <span className={styles.userName}>{user?.name}</span>
                <ChevronDown16Regular />
              </div>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Person24Regular />} onClick={() => navigate('/portal/account')}>
                  My Account
                </MenuItem>
                <MenuItem icon={<Settings24Regular />} onClick={() => navigate('/portal/account/settings')}>
                  Settings
                </MenuItem>
                <MenuDivider />
                <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
                  Sign Out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      {/* Main Content */}
      <main className="portal-main">
        <div className="portal-main-content">
          <Outlet />
        </div>
      </main>

      {/* Chat Windows */}
      {openChatWindows.map((chat, index) => (
        <ChatWindow
          key={chat.id}
          conversation={chat}
          position={index}
          onClose={() => closeChat(chat.id)}
          onMinimize={() => minimizeChat(chat.id)}
        />
      ))}

      {/* Minimized Chats */}
      {minimizedChats.map((chat, index) => (
        <MinimizedChat
          key={chat.id}
          conversation={chat}
          position={index}
          onClick={() => openChat(chat)}
          onClose={() => closeMinimizedChat(chat.id)}
        />
      ))}
    </div>
  );
};

const PortalLayout: React.FC = () => (
  <PortalDebugProvider>
    <ChatProvider>
      <PortalLayoutInner />
    </ChatProvider>
  </PortalDebugProvider>
);

export default PortalLayout;
